/**
 * Fraud Queue Hook
 * 
 * Manages the fraud review queue with:
 * - Real-time WebSocket updates
 * - Filtering and sorting
 * - Review actions (release, deny, request info)
 * - Bulk operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  FraudQueueItem,
  FraudQueueFilters,
  FraudQueueStats,
  FraudPaymentDetail,
  ReviewActionRequest,
  ReviewActionResponse,
  BulkReviewRequest,
  BulkReviewResponse,
  FraudQueueUpdate,
  ReviewerNote,
} from '../types/fraud';

// =============================================================================
// API Functions
// =============================================================================

const fetchFraudQueue = async (
  customerId: string | undefined,
  filters: FraudQueueFilters,
  page: number = 1,
  limit: number = 20
): Promise<{ items: FraudQueueItem[]; total: number; stats: FraudQueueStats }> => {
  const response = await api.get('/payments/fraud-review-queue', {
    params: {
      customerId,
      ...filters,
      page,
      limit,
    },
  });
  return response.data;
};

const fetchFraudPaymentDetail = async (
  paymentId: string
): Promise<FraudPaymentDetail> => {
  const response = await api.get(`/payments/${paymentId}/fraud-details`);
  return response.data;
};

const submitReviewAction = async (
  request: ReviewActionRequest
): Promise<ReviewActionResponse> => {
  const response = await api.post(
    `/payments/${request.paymentId}/review`,
    request
  );
  return response.data;
};

const submitBulkReview = async (
  request: BulkReviewRequest
): Promise<BulkReviewResponse> => {
  const response = await api.post('/payments/bulk-review', request);
  return response.data;
};

const addReviewNote = async (
  paymentId: string,
  content: string
): Promise<ReviewerNote> => {
  const response = await api.post(`/payments/${paymentId}/notes`, { content });
  return response.data;
};

// =============================================================================
// Main Hook
// =============================================================================

interface UseFraudQueueOptions {
  customerId?: string;
  initialFilters?: FraudQueueFilters;
  pageSize?: number;
  enableWebSocket?: boolean;
}

export function useFraudQueue({
  customerId,
  initialFilters = {},
  pageSize = 20,
  enableWebSocket = true,
}: UseFraudQueueOptions = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FraudQueueFilters>(initialFilters);
  const [page, setPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const wsRef = useRef<WebSocket | null>(null);

  // =============================================================================
  // WebSocket Connection
  // =============================================================================

  useEffect(() => {
    if (!enableWebSocket) return;

    const connectWebSocket = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/fraud-queue`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[FraudQueue WS] Connected');
      };

      ws.onmessage = (event) => {
        try {
          const update: FraudQueueUpdate = JSON.parse(event.data);
          handleQueueUpdate(update);
        } catch (error) {
          console.error('[FraudQueue WS] Failed to parse message:', error);
        }
      };

      ws.onclose = () => {
        console.log('[FraudQueue WS] Disconnected');
        // Reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (error) => {
        console.error('[FraudQueue WS] Error:', error);
      };
    };

    connectWebSocket();

    return () => {
      wsRef.current?.close();
    };
  }, [enableWebSocket]);

  const handleQueueUpdate = useCallback((update: FraudQueueUpdate) => {
    switch (update.type) {
      case 'payment_added':
        // Optimistically add to cache
        queryClient.setQueryData(
          ['fraudQueue', filters],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              items: [update.data, ...old.items],
              stats: update.stats || old.stats,
            };
          }
        );
        break;

      case 'payment_removed':
        // Remove from cache
        queryClient.setQueryData(
          ['fraudQueue', filters],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              items: old.items.filter((item: FraudQueueItem) => 
                item.paymentId !== update.paymentId
              ),
              stats: update.stats || old.stats,
            };
          }
        );
        // Remove from selection if present
        setSelectedItems((prev) => {
          const next = new Set(prev);
          next.delete(update.paymentId!);
          return next;
        });
        break;

      case 'payment_updated':
        // Update in cache
        queryClient.setQueryData(
          ['fraudQueue', filters],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              items: old.items.map((item: FraudQueueItem) =>
                item.paymentId === update.paymentId ? update.data : item
              ),
              stats: update.stats || old.stats,
            };
          }
        );
        break;

      case 'stats_updated':
        queryClient.setQueryData(
          ['fraudQueue', filters],
          (old: any) => {
            if (!old) return old;
            return { ...old, stats: update.stats };
          }
        );
        break;
    }
  }, [filters, queryClient]);

  // =============================================================================
  // Queries
  // =============================================================================

  const queueQuery = useQuery({
    queryKey: ['fraudQueue', customerId, filters, page, pageSize],
    queryFn: () => fetchFraudQueue(customerId, filters, page, pageSize),
    refetchInterval: enableWebSocket ? false : 30000, // 30s if no WebSocket
  });

  // =============================================================================
  // Mutations
  // =============================================================================

  const reviewMutation = useMutation({
    mutationFn: submitReviewAction,
    onSuccess: (_data, variables) => {
      // Optimistically update the queue
      queryClient.setQueryData(
        ['fraudQueue', filters],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter(
              (item: FraudQueueItem) => item.paymentId !== variables.paymentId
            ),
          };
        }
      );

      // Remove from selection
      setSelectedItems((prev) => {
        const next = new Set(prev);
        next.delete(variables.paymentId);
        return next;
      });

      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: ['fraudQueue'] });
    },
  });

  const bulkReviewMutation = useMutation({
    mutationFn: submitBulkReview,
    onSuccess: (_data, variables) => {
      // Remove processed items from cache
      queryClient.setQueryData(
        ['fraudQueue', filters],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter(
              (item: FraudQueueItem) => !variables.paymentIds.includes(item.paymentId)
            ),
          };
        }
      );

      // Clear selection
      setSelectedItems(new Set());

      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: ['fraudQueue'] });
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ paymentId, content }: { paymentId: string; content: string }) =>
      addReviewNote(paymentId, content),
    onSuccess: (data, variables) => {
      // Update payment detail cache
      queryClient.setQueryData(
        ['fraudPayment', variables.paymentId],
        (old: FraudPaymentDetail | undefined) => {
          if (!old) return old;
          return {
            ...old,
            notes: [...old.notes, data],
          };
        }
      );
    },
  });

  // =============================================================================
  // Selection Management
  // =============================================================================

  const toggleSelection = useCallback((paymentId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((items: FraudQueueItem[]) => {
    setSelectedItems(new Set(items.map((item) => item.paymentId)));
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const selectRange = useCallback((startId: string, endId: string, items: FraudQueueItem[]) => {
    const startIndex = items.findIndex((item) => item.paymentId === startId);
    const endIndex = items.findIndex((item) => item.paymentId === endId);
    
    if (startIndex === -1 || endIndex === -1) return;
    
    const [min, max] = [Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)];
    const rangeIds = items.slice(min, max + 1).map((item) => item.paymentId);
    
    setSelectedItems((prev) => {
      const next = new Set(prev);
      rangeIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // =============================================================================
  // Actions
  // =============================================================================

  const releasePayment = useCallback(
    async (paymentId: string, reason: string) => {
      return reviewMutation.mutateAsync({
        paymentId,
        action: 'release',
        reason,
      });
    },
    [reviewMutation]
  );

  const denyPayment = useCallback(
    async (paymentId: string, reason: string) => {
      return reviewMutation.mutateAsync({
        paymentId,
        action: 'deny',
        reason,
      });
    },
    [reviewMutation]
  );

  const requestInfo = useCallback(
    async (paymentId: string, reason: string) => {
      return reviewMutation.mutateAsync({
        paymentId,
        action: 'request_info',
        reason,
      });
    },
    [reviewMutation]
  );

  const escalatePayment = useCallback(
    async (paymentId: string, reason: string) => {
      return reviewMutation.mutateAsync({
        paymentId,
        action: 'escalate',
        reason,
      });
    },
    [reviewMutation]
  );

  const bulkRelease = useCallback(
    async (reason: string) => {
      return bulkReviewMutation.mutateAsync({
        paymentIds: Array.from(selectedItems),
        action: 'release',
        reason,
      });
    },
    [bulkReviewMutation, selectedItems]
  );

  const bulkDeny = useCallback(
    async (reason: string) => {
      return bulkReviewMutation.mutateAsync({
        paymentIds: Array.from(selectedItems),
        action: 'deny',
        reason,
      });
    },
    [bulkReviewMutation, selectedItems]
  );

  // =============================================================================
  // Detail View
  // =============================================================================

  const usePaymentDetail = (paymentId: string | null) => {
    return useQuery({
      queryKey: ['fraudPayment', paymentId],
      queryFn: () => fetchFraudPaymentDetail(paymentId!),
      enabled: !!paymentId,
    });
  };

  return {
    // Queue data
    items: queueQuery.data?.items || [],
    total: queueQuery.data?.total || 0,
    stats: queueQuery.data?.stats,
    isLoading: queueQuery.isLoading,
    isError: queueQuery.isError,
    error: queueQuery.error,

    // Pagination
    page,
    setPage,
    pageSize,

    // Filters
    filters,
    setFilters,

    // Selection
    selectedItems,
    selectedCount: selectedItems.size,
    toggleSelection,
    selectAll,
    deselectAll,
    selectRange,
    isSelected: (id: string) => selectedItems.has(id),

    // Actions
    releasePayment,
    denyPayment,
    requestInfo,
    escalatePayment,
    bulkRelease,
    bulkDeny,
    addNote: addNoteMutation.mutate,

    // Mutations state
    isProcessing: reviewMutation.isPending || bulkReviewMutation.isPending,
    processingPaymentId: reviewMutation.variables?.paymentId,

    // Detail view
    usePaymentDetail,

    // Refetch
    refetch: queueQuery.refetch,
  };
}

export default useFraudQueue;
